import type { EditorCommands } from '../editor/commands.js';
import { getOutputNodeInputType, getValueNodeOutputType, isValueNode, type PortType } from '../graph/nodeDefs.js';
import { scopedParamKey } from '../graph/paramKeys.js';
import { resolveGeometrySlotFor, resolveMaterialSlotFor } from '../state/layerGraphSelectors.js';
import type { AppState } from '../state/appState.js';
import type { DataGraphInstance } from '../graph/dataGraph.js';
import type { SceneNodeId } from '../scene/sceneState.js';
import { createPropRow, createSectionHeader } from './sceneTree/widgets.js';
import { dataGraphOutputNames } from '../graph/dataGraph.js';
import { runtimeGraphDefinition } from '../state/runtimeGraphSelectors.js';

interface BindingTarget {
  key: string;
  label: string;
  type: PortType;
  layerId: string;
}

function exposedTargets(appState: AppState, allowedLayerIds?: ReadonlySet<string>): BindingTarget[] {
  const targets: BindingTarget[] = [];
  for (const [layerId, layer] of Object.entries(appState.layerLibrary)) {
    if (allowedLayerIds && !allowedLayerIds.has(layerId)) continue;
    for (const slot of ['geometry', 'material'] as const) {
      const graph = slot === 'geometry'
        ? resolveGeometrySlotFor(appState, layer.geometry)
        : resolveMaterialSlotFor(appState, layer.material);
      if (!graph) continue;
      for (const node of graph.nodes) {
        if (!isValueNode(node) || !node.data.exposed) continue;
        targets.push({
          key: scopedParamKey(layerId, node.id, slot),
          label: `${layer.name || layerId} / ${slot} / ${node.data.name || node.id}`,
          type: getValueNodeOutputType(node),
          layerId,
        });
      }
    }
  }
  return targets;
}

function sceneContainingNode(appState: AppState, nodeId: SceneNodeId): AppState['scene'] | null {
  if (appState.scene.nodes[nodeId]) return appState.scene;
  for (const component of Object.values(appState.componentLibrary)) {
    if (component.scene.nodes[nodeId]) return component.scene;
  }
  return null;
}

function behaviorOwnerName(appState: AppState, nodeId: SceneNodeId): string | null {
  return sceneContainingNode(appState, nodeId)?.nodes[nodeId]?.name ?? null;
}

function bindingLabel(target: BindingTarget, ownerName: string | null): string {
  if (!ownerName) return target.label;
  const prefix = `${ownerName} `;
  return target.label.startsWith(prefix) ? target.label.slice(prefix.length) : target.label;
}

export function behaviorLayerIds(appState: AppState, nodeId: SceneNodeId): Set<string> {
  const result = new Set<string>();
  const initialScene = sceneContainingNode(appState, nodeId);
  if (!initialScene) return result;
  const visit = (scene: AppState['scene'], id: SceneNodeId, ancestors: Set<string>): void => {
    const node = scene.nodes[id];
    if (!node) return;
    if (node.kind === 'layer') {
      result.add(node.layerId);
      return;
    }
    if (node.kind === 'frame') {
      for (const childId of node.childIds) visit(scene, childId, ancestors);
      return;
    }
    if (ancestors.has(node.componentId)) return;
    const component = appState.componentLibrary[node.componentId];
    if (!component) return;
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(node.componentId);
    for (const childId of component.scene.rootIds) visit(component.scene, childId, nextAncestors);
  };
  visit(initialScene, nodeId, new Set());
  return result;
}

function outputType(appState: AppState, graphId: string, output: string): PortType | null {
  const graph = runtimeGraphDefinition(appState, 'data', graphId);
  const node = graph?.nodes.find(candidate => (
    candidate.type === 'Output' && String(candidate.data.inputName || 'Output') === output
  ));
  return node ? getOutputNodeInputType(node) : null;
}

function dataInputNodes(graph: { nodes: AppState['nodes'] }): Array<{ name: string; type: string; defaultValue: unknown }> {
  return graph.nodes
    .filter(node => node.type === 'Input')
    .map(node => ({
      name: String(node.data.name || ''),
      type: String(node.data.valueType || 'number'),
      defaultValue: node.data.default,
    }))
    .filter(input => input.name);
}

function inputText(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function parseInputValue(raw: string, type: string): boolean | number | string | number[] | null {
  if (raw.trim() === 'null') return null;
  if (type === 'string') return raw;
  if (type === 'bool') {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    throw new Error('Boolean values must be true or false');
  }
  if (type === 'number') {
    const value = Number(raw);
    if (!Number.isFinite(value)) throw new Error('Number input must be finite');
    return value;
  }
  const value = JSON.parse(raw);
  const dimensions = Number(type.slice(-1));
  if (!Array.isArray(value) || value.length !== dimensions || value.some(entry => typeof entry !== 'number' || !Number.isFinite(entry))) {
    throw new Error(`Expected a numeric Vector ${dimensions}`);
  }
  return value;
}

export function renderDataBindings(
  panel: HTMLElement,
  appState: AppState,
  commands: Pick<EditorCommands, 'setDataOwner'>,
  owner: { type: 'document' } | { type: 'scene'; nodeId: SceneNodeId },
  instance: DataGraphInstance | null | undefined,
  rerender: () => void,
): void {
  panel.appendChild(createSectionHeader(owner.type === 'document' ? 'data' : 'behavior'));

  const graphRow = createPropRow('behaviorGraph');
  const graphSelect = document.createElement('select');
  graphSelect.className = 'scene-prop-input';
  const none = document.createElement('option');
  none.value = '';
  none.textContent = 'None';
  graphSelect.appendChild(none);
  for (const id of Object.keys(appState.dataGraphs).sort()) {
    const option = document.createElement('option');
    option.value = id;
    option.textContent = id;
    graphSelect.appendChild(option);
  }
  graphSelect.value = instance?.use ?? '';
  graphSelect.addEventListener('change', () => {
    commands.setDataOwner(owner, graphSelect.value ? { use: graphSelect.value, bindings: {} } : null);
    rerender();
  });
  graphRow.appendChild(graphSelect);
  panel.appendChild(graphRow);

  if (!instance || !appState.dataGraphs[instance.use]) return;
  const targets = exposedTargets(
    appState,
    owner.type === 'scene' ? behaviorLayerIds(appState, owner.nodeId) : undefined,
  );
  const ownerName = owner.type === 'scene' ? behaviorOwnerName(appState, owner.nodeId) : null;
  const graph = runtimeGraphDefinition(appState, 'data', instance.use)!;
  const inputs = dataInputNodes(graph);
  if (inputs.length) {
    const inputHeader = createSectionHeader('data');
    inputHeader.textContent = 'INPUTS';
    panel.appendChild(inputHeader);
    const inputValues = instance.inputs ?? {};
    for (const input of inputs) {
      const row = document.createElement('div');
      row.className = 'scene-prop-row data-input-row';
      const label = document.createElement('label');
      label.className = 'scene-prop-label';
      label.textContent = input.name;
      label.title = `Data input (${input.type})`;
      const field = document.createElement('input');
      field.type = 'text';
      field.className = 'scene-prop-input';
      field.value = inputText(inputValues[input.name] ?? input.defaultValue);
      field.title = `${input.type}; use null to clear`;
      field.addEventListener('change', () => {
        try {
          const nextInputs = { ...inputValues, [input.name]: parseInputValue(field.value, input.type) };
          commands.setDataOwner(owner, { ...instance, inputs: nextInputs });
          rerender();
        } catch (error) {
          field.classList.add('invalid');
          field.title = error instanceof Error ? error.message : 'Invalid input value';
        }
      });
      row.append(label, field);
      panel.appendChild(row);
    }
  }
  const outputs = dataGraphOutputNames(graph);
  const updateBinding = (output: string, target: string): void => {
    const bindings = { ...instance.bindings };
    if (target) bindings[output] = target;
    else delete bindings[output];
    commands.setDataOwner(owner, { ...instance, bindings });
    rerender();
  };

  const boundOutputs = outputs.filter(output => output in instance.bindings);
  if (boundOutputs.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'data-binding-empty';
    empty.textContent = 'No outputs are bound';
    panel.appendChild(empty);
  }

  for (const output of boundOutputs) {
    const row = document.createElement('div');
    row.className = 'scene-prop-row data-binding-row';
    const label = document.createElement('span');
    label.className = 'scene-prop-label';
    label.title = output;
    // Keep legacy default graphs visually consistent with current Output naming.
    label.textContent = output === 'output' ? 'Output' : output;
    const select = document.createElement('select');
    select.className = 'scene-prop-input';
    const empty = document.createElement('option');
    empty.value = '';
    empty.textContent = 'Choose target';
    select.appendChild(empty);
    const type = outputType(appState, instance.use, output);
    for (const target of targets.filter(candidate => !type || candidate.type === type)) {
      const option = document.createElement('option');
      option.value = target.key;
      option.textContent = bindingLabel(target, ownerName);
      select.appendChild(option);
    }
    const currentTarget = instance.bindings[output] ?? '';
    if (currentTarget && !targets.some(target => target.key === currentTarget)) {
      const missing = document.createElement('option');
      missing.value = currentTarget;
      missing.textContent = owner.type === 'scene' ? 'Outside this behavior' : 'Missing exposed parameter';
      select.appendChild(missing);
    }
    select.value = currentTarget;
    select.addEventListener('change', () => updateBinding(output, select.value));
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'scene-tree-btn data-binding-remove';
    remove.textContent = '×';
    remove.title = `Remove ${output} binding`;
    remove.setAttribute('aria-label', remove.title);
    remove.addEventListener('click', () => updateBinding(output, ''));
    row.append(label, select, remove);
    panel.appendChild(row);
  }

  const unboundOutputs = outputs.filter(output => !(output in instance.bindings));
  if (unboundOutputs.length === 0) return;
  const addRow = createPropRow('source');
  addRow.classList.add('data-binding-add-row');
  addRow.firstElementChild!.textContent = 'Add binding';
  addRow.firstElementChild!.setAttribute('title', 'Add binding');
  const outputSelect = document.createElement('select');
  outputSelect.className = 'scene-prop-input data-binding-output-select';
  outputSelect.appendChild(new Option('Choose output', ''));
  for (const output of unboundOutputs) {
    outputSelect.appendChild(new Option(output === 'output' ? 'Output' : output, output));
  }
  const targetSelect = document.createElement('select');
  targetSelect.className = 'scene-prop-input data-binding-target-select';
  const refillTargets = (): void => {
    const output = outputSelect.value;
    targetSelect.replaceChildren(new Option(output ? 'Choose target' : 'Choose output first', ''));
    const type = output ? outputType(appState, instance.use, output) : null;
    for (const target of targets.filter(candidate => !type || candidate.type === type)) {
      targetSelect.appendChild(new Option(bindingLabel(target, ownerName), target.key));
    }
    targetSelect.disabled = !output;
  };
  outputSelect.addEventListener('change', refillTargets);
  targetSelect.addEventListener('change', () => {
    if (outputSelect.value && targetSelect.value) updateBinding(outputSelect.value, targetSelect.value);
  });
  refillTargets();
  addRow.append(outputSelect, targetSelect);
  panel.appendChild(addRow);
}
